/**
 * newsfeedApi — single RTK Query endpoint backing the Admin Newsfeed widget.
 *
 * Injects into rootApi so the widget shares the existing Supabase baseQuery
 * and reducerPath. Tagged `ChangeLog` (already registered on rootApi) so the
 * Realtime hook can `invalidateTags` — but in practice the Realtime handler
 * patches the cache in place via `updateQueryData`, matching the pessimistic
 * pattern in `.agent/workflows/feature-restructure.md`.
 */
import { rootApi } from "../../../lib/api/rootApi";
import type { NewsfeedApiArgs, NewsfeedApiPage } from "./newsfeedApiTypes";

export const newsfeedApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminNewsfeed: builder.query<NewsfeedApiPage, NewsfeedApiArgs | void>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const limit = args?.limit ?? 50;
        const before = args?.before ?? null;
        const from = args?.from ?? null;
        const to = args?.to ?? null;
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_admin_newsfeed_recent",
          params: {
            p_limit: limit,
            p_before: before,
            p_from: from,
            p_to: to,
          },
        });
        if (error) return { error };
        const payload = (data ?? { items: [], has_more: false }) as NewsfeedApiPage;
        return { data: payload };
      },
      providesTags: ["ChangeLog"],
      // Cache the feed for the session; Realtime keeps it warm.
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetAdminNewsfeedQuery } = newsfeedApi;
